import { useEffect, useState, forwardRef } from "react";

import {
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  $isListNode,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Stack, Theme } from "@mui/material";
import { $getSelection, $isRangeSelection, ElementNode, FORMAT_TEXT_COMMAND, TextNode } from "lexical";

import { DashboardIconButton } from "@/dashboard-design-system/IconButton";
import { Bold, Italic, OrderedList, Underline, UnorderedList } from "@/icons/outlined";

type FloatingToolbarProps = {
  editor: ReturnType<typeof useLexicalComposerContext>[0];
};

export const FloatingToolbar = forwardRef<HTMLDivElement, FloatingToolbarProps>(({ editor }, ref) => {
  const [toolbarState, setToolbarState] = useState({
    isBold: false,
    isItalic: false,
    isUnderline: false,
    isOrderedList: false,
    isUnorderedList: false,
  });

  useEffect(() => {
    function getListTypeFromNode(node: ElementNode | TextNode) {
      let current: ElementNode | TextNode | null = node;
      while (current !== null) {
        if ($isListNode(current)) {
          return current.getListType();
        }
        current = current.getParent();
      }
      return null;
    }

    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();

        if (!$isRangeSelection(selection)) {
          return;
        }

        const listType = getListTypeFromNode(selection.anchor.getNode());

        setToolbarState({
          isBold: selection.hasFormat("bold"),
          isItalic: selection.hasFormat("italic"),
          isUnderline: selection.hasFormat("underline"),
          isOrderedList: listType === "number",
          isUnorderedList: listType === "bullet",
        });
      });
    });
  }, [editor]);

  const styles = (theme: Theme) => ({
    backgroundColor: theme.palette.surface.neutral.white,
    border: `1px solid ${theme.palette.border.neutral.quiet}`,
    borderRadius: theme.radius(1),
  });

  return (
    <Stack ref={ref} direction="row" sx={styles}>
      <DashboardIconButton
        variant="text"
        vibe={toolbarState.isBold ? "brand" : "neutral"}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}
      >
        <Bold />
      </DashboardIconButton>
      <DashboardIconButton
        variant="text"
        vibe={toolbarState.isItalic ? "brand" : "neutral"}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")}
      >
        <Italic />
      </DashboardIconButton>
      <DashboardIconButton
        variant="text"
        vibe={toolbarState.isUnderline ? "brand" : "neutral"}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")}
      >
        <Underline />
      </DashboardIconButton>
      <DashboardIconButton
        variant="text"
        vibe={toolbarState.isUnorderedList ? "brand" : "neutral"}
        onClick={() =>
          editor.dispatchCommand(
            toolbarState.isUnorderedList ? REMOVE_LIST_COMMAND : INSERT_UNORDERED_LIST_COMMAND,
            undefined
          )
        }
      >
        <UnorderedList />
      </DashboardIconButton>
      <DashboardIconButton
        variant="text"
        vibe={toolbarState.isOrderedList ? "brand" : "neutral"}
        onClick={() =>
          editor.dispatchCommand(
            toolbarState.isOrderedList ? REMOVE_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND,
            undefined
          )
        }
      >
        <OrderedList />
      </DashboardIconButton>
    </Stack>
  );
});

FloatingToolbar.displayName = "FloatingToolbar";
